import { useCallback, useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
} from "../components/icons";
import { trpc } from "../trpc";
import type { AiProviderItem, RemoteAiModelItem } from "../../shared/aiTypes";

const { Title, Text } = Typography;

export function AiProviderPage() {
  const { message } = App.useApp();
  const [providers, setProviders] = useState<AiProviderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AiProviderItem | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [form] = Form.useForm();

  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelProvider, setModelProvider] = useState<AiProviderItem | null>(null);
  const [remoteModels, setRemoteModels] = useState<RemoteAiModelItem[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [selectedRemoteIds, setSelectedRemoteIds] = useState<string[]>([]);
  const [modelSearchKeyword, setModelSearchKeyword] = useState("");
  const [importLoading, setImportLoading] = useState(false);

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testProvider, setTestProvider] = useState<AiProviderItem | null>(null);
  const [testModels, setTestModels] = useState<RemoteAiModelItem[]>([]);
  const [testModelsLoading, setTestModelsLoading] = useState(false);
  const [testRemoteId, setTestRemoteId] = useState<string>();
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; text?: string; error?: string } | null>(null);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trpc.aiProvider.list.query();
      setProviders(data);
    } catch {
      message.error("加载供应商失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleCreate = () => {
    setEditingProvider(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true, proxyUrl: "" });
    setEditOpen(true);
  };

  const handleEdit = async (record: AiProviderItem) => {
    setEditingProvider(record);
    try {
      const detail = await trpc.aiProvider.get.query({ providerId: record.providerId });
      form.setFieldsValue({
        providerId: detail.providerId,
        name: detail.name,
        baseUrl: detail.baseUrl,
        apiKey: detail.apiKey,
        proxyUrl: detail.proxyUrl,
        enabled: detail.enabled,
      });
      setEditOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "加载供应商详情失败");
    }
  };

  const handleSave = async (values: {
    providerId?: string;
    name: string;
    baseUrl: string;
    apiKey?: string;
    proxyUrl?: string;
    enabled: boolean;
  }) => {
    setSaveLoading(true);
    try {
      await trpc.aiProvider.upsert.mutate(values);
      message.success("供应商保存成功");
      setEditOpen(false);
      form.resetFields();
      setEditingProvider(null);
      loadProviders();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "供应商保存失败");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (record: AiProviderItem) => {
    try {
      await trpc.aiProvider.delete.mutate({ providerId: record.providerId });
      message.success("删除成功");
      loadProviders();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "删除失败");
    }
  };

  const openModelImport = async (record: AiProviderItem) => {
    setModelProvider(record);
    setModelModalOpen(true);
    setRemoteModels([]);
    setSelectedRemoteIds([]);
    setModelSearchKeyword("");
    setRemoteLoading(true);
    try {
      const data = await trpc.aiProvider.fetchRemoteModels.query({ providerId: record.providerId });
      setRemoteModels(data);
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "获取远程模型失败");
      setModelModalOpen(false);
    } finally {
      setRemoteLoading(false);
    }
  };

  const openTestModal = async (record: AiProviderItem) => {
    setTestProvider(record);
    setTestModalOpen(true);
    setTestModels([]);
    setTestRemoteId(undefined);
    setTestResult(null);
    setTestModelsLoading(true);
    try {
      const data = await trpc.aiProvider.fetchRemoteModels.query({ providerId: record.providerId });
      setTestModels(data);
      if (data[0]?.id) {
        setTestRemoteId(data[0].id);
      }
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "获取模型列表失败");
      setTestModalOpen(false);
    } finally {
      setTestModelsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testProvider || !testRemoteId) {
      message.warning("请先选择模型");
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await trpc.aiProvider.test.mutate({
        providerId: testProvider.providerId,
        remoteId: testRemoteId,
      });
      setTestResult({ success: true, text: result.text });
      message.success("供应商测试通过");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "供应商测试失败";
      setTestResult({ success: false, error: errorMessage });
      message.error(errorMessage);
    } finally {
      setTestLoading(false);
    }
  };

  const handleImportModels = async () => {
    if (!modelProvider) return;
    const toImport = selectedRemoteIds.filter((id) => {
      const item = remoteModels.find((model) => model.id === id);
      return item && !item.alreadyImported;
    });
    if (toImport.length === 0) {
      message.warning("请选择尚未导入的模型");
      return;
    }

    setImportLoading(true);
    try {
      const result = await trpc.aiProvider.importModels.mutate({
        providerId: modelProvider.providerId,
        remoteIds: toImport,
      });
      message.success(`成功导入 ${result.count} 个模型`);
      setModelModalOpen(false);
      setModelProvider(null);
      loadProviders();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "导入模型失败");
    } finally {
      setImportLoading(false);
    }
  };

  const columns: ColumnsType<AiProviderItem> = [
    {
      title: "名称",
      dataIndex: "name",
      key: "name",
      width: 160,
    },
    {
      title: "Base URL",
      dataIndex: "baseUrl",
      key: "baseUrl",
      ellipsis: true,
    },
    {
      title: "API Key",
      dataIndex: "apiKeyMasked",
      key: "apiKeyMasked",
      width: 180,
    },
    {
      title: "代理",
      dataIndex: "proxyUrlMasked",
      key: "proxyUrlMasked",
      width: 190,
      ellipsis: true,
      render: (proxyUrlMasked: string) =>
        proxyUrlMasked ? <Text code>{proxyUrlMasked}</Text> : <Text type="secondary">直连</Text>,
    },
    {
      title: "模型数",
      dataIndex: "modelCount",
      key: "modelCount",
      width: 90,
    },
    {
      title: "状态",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      render: (enabled: boolean) => (
        <Tag color={enabled ? "success" : "default"}>{enabled ? "启用" : "禁用"}</Tag>
      ),
    },
    {
      title: "操作",
      key: "actions",
      width: 220,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<SendOutlined />}
            onClick={() => openTestModal(record)}
            title="测试"
          />
          <Button
            type="text"
            size="small"
            icon={<CloudDownloadOutlined />}
            onClick={() => openModelImport(record)}
            title="获取模型"
          />
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="删除供应商"
            description={`确定要删除 "${record.name}" 吗？`}
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <Card
        variant="borderless"
        className="shadow-sm"
        title={
          <Title level={5} style={{ margin: 0 }}>
            AI 供应商管理
          </Title>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadProviders} loading={loading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新增供应商
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={providers}
          rowKey="providerId"
          loading={loading}
          scroll={{ x: 1180 }}
          locale={{
            emptyText: (
              <Spin spinning={loading}>
                <div style={{ padding: "40px 0" }}>暂无供应商</div>
              </Spin>
            ),
          }}
        />
      </Card>

      <Modal
        title={editingProvider ? "编辑供应商" : "新增供应商"}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          form.resetFields();
          setEditingProvider(null);
        }}
        footer={null}
        destroyOnHidden
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="providerId" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入供应商名称" }]}>
            <Input placeholder="如 OpenAI 兼容网关" />
          </Form.Item>

          <Form.Item
            name="baseUrl"
            label="Base URL"
            rules={[{ required: true, message: "请输入 Base URL" }]}
            extra="需兼容 OpenAI API，填写含 /v1 的根地址，如 https://api.openai.com/v1"
          >
            <Input placeholder="https://api.openai.com/v1" />
          </Form.Item>

          <Form.Item
            name="apiKey"
            label="API Key"
            rules={editingProvider ? [] : [{ required: true, message: "请输入 API Key" }]}
            extra={editingProvider ? "留空则保持原 API Key 不变" : undefined}
          >
            <Input.Password placeholder="sk-..." />
          </Form.Item>

          <Form.Item
            name="proxyUrl"
            label="代理 URL"
            rules={[
              {
                validator: async (_, value?: string) => {
                  const proxyUrl = value?.trim();
                  if (!proxyUrl) return;
                  try {
                    const protocol = new URL(proxyUrl).protocol;
                    if (!["http:", "https:", "socks5:", "socks5h:"].includes(protocol)) {
                      throw new Error();
                    }
                  } catch {
                    throw new Error(
                      "请输入 http://、https://、socks5:// 或 socks5h:// 开头的代理 URL"
                    );
                  }
                },
              },
            ]}
            extra="留空表示直连；可包含认证信息，如 socks5://user:pass@127.0.0.1:1080"
          >
            <Input.Password placeholder="http://127.0.0.1:7890" autoComplete="off" />
          </Form.Item>

          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setEditOpen(false);
                  form.resetFields();
                  setEditingProvider(null);
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modelProvider ? `从「${modelProvider.name}」获取模型` : "获取模型"}
        open={modelModalOpen}
        onCancel={() => {
          setModelModalOpen(false);
          setModelProvider(null);
          setModelSearchKeyword("");
        }}
        onOk={handleImportModels}
        okText="导入选中模型"
        confirmLoading={importLoading}
        width={720}
        destroyOnHidden
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          通过供应商的 /v1/models 接口拉取模型列表，勾选需要加入系统的模型。Base URL 需包含 /v1。
        </Text>
        <Input.Search
          allowClear
          placeholder="搜索模型 ID 或提供方"
          value={modelSearchKeyword}
          onChange={(e) => setModelSearchKeyword(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          rowKey="id"
          loading={remoteLoading}
          dataSource={remoteModels.filter((item) => {
            const keyword = modelSearchKeyword.trim().toLowerCase();
            if (!keyword) return true;
            return (
              item.id.toLowerCase().includes(keyword) ||
              item.ownedBy.toLowerCase().includes(keyword)
            );
          })}
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: selectedRemoteIds,
            preserveSelectedRowKeys: true,
            onChange: (keys) => setSelectedRemoteIds(keys as string[]),
            getCheckboxProps: (record) => ({
              disabled: record.alreadyImported,
            }),
          }}
          columns={[
            { title: "模型 ID", dataIndex: "id", key: "id" },
            { title: "提供方", dataIndex: "ownedBy", key: "ownedBy", width: 160 },
            {
              title: "状态",
              key: "status",
              width: 100,
              render: (_, record) =>
                record.alreadyImported ? <Tag color="blue">已导入</Tag> : <Tag>未导入</Tag>,
            },
          ]}
        />
      </Modal>

      <Modal
        title={testProvider ? `测试「${testProvider.name}」` : "测试供应商"}
        open={testModalOpen}
        onCancel={() => {
          setTestModalOpen(false);
          setTestProvider(null);
          setTestResult(null);
        }}
        onOk={handleTest}
        okText="发送 hi 测试"
        confirmLoading={testLoading}
        okButtonProps={{ disabled: !testRemoteId || testModelsLoading }}
        width={560}
        destroyOnHidden
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          选择一个模型，发送「hi」验证供应商是否可用。
        </Text>

        <div style={{ marginBottom: 16 }}>
          <Text style={{ display: "block", marginBottom: 8 }}>测试模型</Text>
          <Select
            style={{ width: "100%" }}
            placeholder="请选择模型"
            loading={testModelsLoading}
            value={testRemoteId}
            onChange={setTestRemoteId}
            options={testModels.map((item) => ({
              label: item.id,
              value: item.id,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </div>

        {testResult && (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              background: testResult.success ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)",
              border: `1px solid ${testResult.success ? "rgba(34, 197, 94, 0.25)" : "rgba(239, 68, 68, 0.25)"}`,
            }}
          >
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              {testResult.success ? "测试通过" : "测试失败"}
            </Text>
            <Text style={{ whiteSpace: "pre-wrap" }}>
              {testResult.success ? testResult.text : testResult.error}
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
}
